import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, DatePicker, InputNumber, Button, message, Space, Typography, Select } from 'antd';
import { CheckCircleOutlined, DollarOutlined, FileTextOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiClient from '../../services/api';
import { VALIDATION_MESSAGES, SUCCESS_MESSAGES, ERROR_MESSAGES } from '../../constants/assetWork';

const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

interface CompleteWorkRecordModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  workRecord: {
    id: number;
    asset_code: string;
    asset_name: string;
    maintenance_type_name: string;
    work_description?: string;
    status: string;
    asset_id?: number;
  } | null;
  companyId?: number;
}

const CompleteWorkRecordModal: React.FC<CompleteWorkRecordModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  workRecord,
  companyId
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState<Array<{
    id: number; 
    name?: string; 
    employee_code?: string;
    full_name?: string;
    first_name?: string;
    last_name?: string;
    display_name?: string;
  }>>([]);
  const [suppliers, setSuppliers] = useState<Array<{id: number, name: string, supplier_code?: string}>>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [assetUser, setAssetUser] = useState<{id: number, name: string} | null>(null);
  const [loadingAssetUser, setLoadingAssetUser] = useState(false);

  // Lấy danh sách nhân viên
  const fetchEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const params = companyId ? { company_id: companyId } : {};
      const response = await apiClient.getEmployees(params);
      const employeesData = Array.isArray(response) ? response : [];
      
      setEmployees(employeesData);
    } catch (error: any) {
      console.error('Error fetching employees:', error);
      message.error(ERROR_MESSAGES.LOAD_EMPLOYEES_FAILED);
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Lấy danh sách nhà cung cấp
  const fetchSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      const response = await apiClient.getSuppliers();
      setSuppliers(Array.isArray(response) ? response : []);
    } catch (error: any) {
      message.error(ERROR_MESSAGES.LOAD_SUPPLIERS_FAILED);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  // Lấy nhân viên đang sử dụng tài sản
  const fetchAssetUser = async () => {
    if (!workRecord?.asset_id) return;
    
    try {
      setLoadingAssetUser(true);
      const asset = await apiClient.getAsset(workRecord.asset_id);
      console.log('🏷️ Asset data:', asset);
      
      if (asset && asset.assigned_to_employee_id) {
        // Tìm nhân viên trong danh sách đã load
        const user = employees.find(emp => emp.id === asset.assigned_to_employee_id);
        if (user) {
          const userName = user.name || 
                          user.full_name || 
                          user.display_name || 
                          `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
                          'Không có tên';
          setAssetUser({ id: user.id, name: userName });
          console.log('👤 Asset user found:', { id: user.id, name: userName });
        }
      }
    } catch (error: any) {
      console.error('❌ Error fetching asset user:', error);
    } finally {
      setLoadingAssetUser(false);
    }
  };

  // Load data khi modal mở
  useEffect(() => {
    if (visible) {
      console.log('🚀 Modal opened, companyId:', companyId, 'workRecord:', workRecord);
      fetchEmployees();
      fetchSuppliers();
    }
  }, [visible, companyId]);

  // Fetch asset user sau khi đã load employees
  useEffect(() => {
    if (visible && employees.length > 0 && workRecord?.asset_id) {
      fetchAssetUser();
    }
  }, [visible, employees, workRecord?.asset_id]);

  // Set giá trị mặc định khi có asset user
  useEffect(() => {
    if (assetUser) {
      form.setFieldsValue({ performed_by_employee_id: assetUser.id });
      console.log('✅ Set default employee:', assetUser);
    }
  }, [assetUser, form]);

  const handleSubmit = async (values: any) => {
    if (!workRecord) return;

    setLoading(true);
    try {
      await apiClient.completeWorkRecord(workRecord.id, {
        completed_date: values.completed_date.format('YYYY-MM-DD'),
        cost: Number(values.cost) || 0,
        notes: values.notes,
        work_documents: values.work_documents,
        performed_by_employee_id: values.performed_by_employee_id,
        performed_by_supplier_id: values.performed_by_supplier_id
      });

      form.resetFields();
      onSuccess();
      onCancel();
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || ERROR_MESSAGES.COMPLETE_FAILED;
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={
        <Space>
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
          <span>Hoàn thành tác nghiệp</span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnClose
    >


      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          completed_date: dayjs(),
          cost: 0
        }}
      >
        <Form.Item
          label="Ngày hoàn thành"
          name="completed_date"
          rules={[{ required: true, message: VALIDATION_MESSAGES.COMPLETION_DATE_REQUIRED }]}
        >
          <DatePicker 
            style={{ width: '100%' }} 
            format="DD/MM/YYYY"
            placeholder="Chọn ngày hoàn thành"
          />
        </Form.Item>

        <Form.Item
          label="Chi phí (VNĐ)"
          name="cost"
          rules={[{ type: 'number', min: 0, message: VALIDATION_MESSAGES.COST_INVALID }]}
        >
          <InputNumber
            style={{ width: '100%' }}
            placeholder="Nhập chi phí thực tế"
            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(value: any) => {
              if (!value) return 0;
              const cleanValue = value.replace(/\$\s?|(,*)/g, '');
              const parsed = parseInt(cleanValue);
              return isNaN(parsed) ? 0 : parsed;
            }}
            min={0}
            addonAfter={<DollarOutlined />}
          />
        </Form.Item>

        <Form.Item
          label="Ghi chú hoàn thành"
          name="notes"
        >
          <TextArea
            rows={3}
            placeholder="Nhập ghi chú về việc hoàn thành tác nghiệp"
          />
        </Form.Item>

        <Form.Item
          label="Mã hồ sơ hoàn thành"
          name="work_documents"
        >
          <Input
            placeholder="Nhập mã hồ sơ hoàn thành"
            prefix={<FileTextOutlined />}
          />
        </Form.Item>

        <Form.Item
          label="Nhân viên thực hiện"
          name="performed_by_employee_id"
        >
          <Select
            loading={loadingEmployees}
            placeholder="Chọn nhân viên thực hiện"
            style={{ width: '100%' }}
            showSearch
            filterOption={(input, option) =>
              (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
            }
          >
            {employees.length > 0 ? (
              employees.map(employee => {
                // Thử các field khác nhau cho tên nhân viên
                const employeeName = employee.name || 
                                   employee.full_name || 
                                   employee.display_name || 
                                   `${employee.first_name || ''} ${employee.last_name || ''}`.trim() ||
                                   'Không có tên';
                
                return (
                  <Option key={employee.id} value={employee.id}>
                    {employee.employee_code ? `${employee.employee_code} - ` : ''}{employeeName}
                  </Option>
                );
              })
            ) : (
              <Option value="" disabled>
                {loadingEmployees ? 'Đang tải...' : 'Không có nhân viên'}
              </Option>
            )}
          </Select>
        </Form.Item>

        <Form.Item
          label="Nhà cung cấp (nếu có)"
          name="performed_by_supplier_id"
        >
          <Select
            loading={loadingSuppliers}
            placeholder="Chọn nhà cung cấp (nếu thuê ngoài)"
            style={{ width: '100%' }}
            showSearch
            filterOption={(input, option) =>
              (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
            }
            allowClear
          >
            {suppliers.map(supplier => (
              <Option key={supplier.id} value={supplier.id}>
                {supplier.supplier_code ? `${supplier.supplier_code} - ` : ''}{supplier.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={handleCancel}>
              Hủy
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              icon={<CheckCircleOutlined />}
            >
              Hoàn thành tác nghiệp
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CompleteWorkRecordModal; 